import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import {
  SourcingRequest,
  TripInquiry,
  inquiryError,
  inquiryLabel,
  inquiryMoney,
  tripSourcingAPI,
} from '../../services/tripInquiry';

function RequestCard({
  request,
  inquiry,
  onChange,
}: {
  request: SourcingRequest;
  inquiry?: TripInquiry;
  onChange: () => void;
}) {
  const [cost, setCost] = useState('');
  const [expiry, setExpiry] = useState('');
  const [conditions, setConditions] = useState('');
  const [capacity, setCapacity] = useState(false);
  const [actionId, setActionId] = useState(() => crypto.randomUUID());
  const action = useMutation((task: () => Promise<unknown>) => task(), {
    onSuccess: () => {
      setActionId(crypto.randomUUID());
      onChange();
    },
  });
  const selected = request.proposals.find(
    (proposal) => proposal.id === request.selectedProposalId
  );
  return (
    <article className="admin-subpanel rounded-card p-5 space-y-4">
      <div className="flex justify-between gap-4">
        <div>
          <h3 className="font-semibold">
            {request.pickupArea} → {request.dropoffArea}
          </h3>
          <p className="text-xs text-on-surface-variant">
            {request.originName} ·{' '}
            {new Date(request.startAt).toLocaleString(undefined, {
              timeZone: request.timezone,
            })}{' '}
            · {request.timezone}
          </p>
        </div>
        <span className="text-sm">{inquiryLabel(request.state)}</span>
      </div>
      <p className="text-sm whitespace-pre-wrap">{request.requirements}</p>
      {request.owner ? (
        <>
          <p className="text-xs text-on-surface-variant">
            Provider costs below are private to your team. Set the customer
            price in the linked quote.
          </p>
          <div className="space-y-3">
            {request.proposals.map((proposal) => (
              <div
                key={proposal.id}
                className="border border-outline-variant p-4 space-y-2"
              >
                <div className="flex justify-between">
                  <strong>{proposal.providerName}</strong>
                  <strong>{inquiryMoney(proposal.amountCents / 100)}</strong>
                </div>
                <p className="text-xs">
                  Valid until {new Date(proposal.validUntil).toLocaleString()} ·{' '}
                  {proposal.capacityConfirmed
                    ? 'Capacity confirmed by provider'
                    : 'Capacity not confirmed'}
                </p>
                <p className="text-sm whitespace-pre-wrap">
                  {proposal.conditions}
                </p>
                {request.selectedProposalId === proposal.id ? (
                  <p className="text-sm text-primary">
                    Selected provider proposal
                  </p>
                ) : (
                  request.state === 'open' && (
                    <button
                      className="btn-secondary"
                      disabled={
                        action.isLoading ||
                        !proposal.capacityConfirmed ||
                        new Date(proposal.validUntil).getTime() <= Date.now()
                      }
                      onClick={() =>
                        action.mutate(() =>
                          tripSourcingAPI.select(
                            request.id,
                            proposal.id,
                            request.version,
                            actionId
                          )
                        )
                      }
                    >
                      Select this proposal
                    </button>
                  )
                )}
              </div>
            ))}
            {!request.proposals.length && (
              <p className="text-sm text-on-surface-variant">
                Waiting for participating operators to respond.
              </p>
            )}
          </div>
          {selected &&
            inquiry?.active_quote_id &&
            !inquiry.converted_reservation_id && (
              <button
                className="btn-primary"
                disabled={action.isLoading}
                onClick={() =>
                  action.mutate(() =>
                    tripSourcingAPI.approve(inquiry.id, {
                      quoteId: inquiry.active_quote_id!,
                      expectedVersion: inquiry.version,
                      fulfillment: 'partner',
                      proposalId: selected.id,
                    })
                  )
                }
              >
                Approve quote with selected partner
              </button>
            )}
          {inquiry?.converted_reservation_id &&
            request.state === 'selected' && (
              <button
                className="btn-primary"
                disabled={action.isLoading}
                onClick={() =>
                  action.mutate(() =>
                    tripSourcingAPI.adopt(request.id, request.version, actionId)
                  )
                }
              >
                Create confirmed farm-out handoff
              </button>
            )}
          {request.adoptedOfferId && (
            <Link
              className="text-primary underline text-sm"
              to="/admin/farm-outs"
            >
              Continue in Farm-outs
            </Link>
          )}
        </>
      ) : (
        <form
          className="space-y-4 border-t border-outline-variant pt-4"
          onSubmit={(event) => {
            event.preventDefault();
            action.mutate(() =>
              tripSourcingAPI.propose(request.id, {
                requestId: actionId,
                expectedVersion: request.version,
                amountCents: Math.round(Number(cost) * 100),
                currency: 'USD',
                validUntil: new Date(expiry).toISOString(),
                conditions,
                capacityConfirmed: capacity,
              })
            );
          }}
        >
          <h4 className="font-semibold">Provide capacity and a proposal</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="text-sm">
              Your cost (USD)
              <input
                type="number"
                min="0.01"
                step="0.01"
                required
                className="input-field w-full mt-2"
                value={cost}
                onChange={(event) => setCost(event.target.value)}
              />
            </label>
            <label className="text-sm">
              Proposal valid until (your local time)
              <input
                type="datetime-local"
                required
                className="input-field w-full mt-2"
                value={expiry}
                onChange={(event) => setExpiry(event.target.value)}
              />
            </label>
          </div>
          <label className="text-sm block">
            Conditions and service details
            <textarea
              required
              maxLength={2000}
              rows={3}
              className="input-field w-full mt-2"
              value={conditions}
              onChange={(event) => setConditions(event.target.value)}
            />
          </label>
          <label className="text-sm flex gap-2 items-center">
            <input
              type="checkbox"
              checked={capacity}
              onChange={(event) => setCapacity(event.target.checked)}
            />{' '}
            I confirm capacity for this itinerary and window
          </label>
          <button
            className="btn-primary"
            disabled={action.isLoading || request.state !== 'open'}
          >
            Submit provider proposal
          </button>
          <p className="text-xs text-on-surface-variant">
            A proposal does not dispatch a trip. Confirmed bookings continue
            through the existing farm-out handoff.
          </p>
        </form>
      )}
      {!!action.error && (
        <p role="alert" className="text-error text-sm">
          {inquiryError(action.error)}{' '}
          <button className="underline" onClick={onChange}>
            Refresh current state
          </button>
        </p>
      )}
    </article>
  );
}
export function OpportunitySourcing({
  inquiry,
  onChange,
}: {
  inquiry: TripInquiry;
  onChange: () => void;
}) {
  const cache = useQueryClient();
  const [publishOpen, setPublishOpen] = useState(false);
  const [pickupArea, setPickupArea] = useState('');
  const [dropoffArea, setDropoffArea] = useState('');
  const [requirements, setRequirements] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const list = useQuery(['trip-sourcing', 'owned'], () =>
    tripSourcingAPI.list('owned').then((response) => response.data.data)
  );
  const refresh = () => {
    cache.invalidateQueries('trip-sourcing');
    onChange();
  };
  const publish = useMutation(
    () =>
      tripSourcingAPI.publish({
        inquiryId: inquiry.id,
        requestId,
        expectedVersion: inquiry.version,
        requirements,
        pickupArea,
        dropoffArea,
      }),
    {
      onSuccess: () => {
        setPublishOpen(false);
        setRequestId(crypto.randomUUID());
        refresh();
      },
    }
  );
  const requests = Array.isArray(list.data)
    ? list.data.filter((request) => request.inquiryId === inquiry.id)
    : [];
  return (
    <section className="space-y-4 border-t border-outline-variant pt-6">
      <h3 className="font-semibold">Partner capacity</h3>
      <p className="text-sm text-on-surface-variant">
        Source capacity before booking. Share coarse service areas and
        operational requirements; exact addresses and requester contact details
        stay private.
      </p>
      {!!list.error && (
        <p role="alert" className="text-error">
          {inquiryError(list.error)}
        </p>
      )}
      {requests.map((request) => (
        <RequestCard
          key={`${request.id}:${request.version}`}
          request={request}
          inquiry={inquiry}
          onChange={refresh}
        />
      ))}
      {!inquiry.converted_reservation_id && !requests.length && (
        <button
          className="btn-secondary"
          onClick={() => setPublishOpen((current) => !current)}
        >
          Request partner capacity
        </button>
      )}
      {publishOpen && (
        <form
          className="admin-subpanel rounded-card p-5 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            publish.mutate();
          }}
        >
          <div className="grid md:grid-cols-2 gap-4">
            <label className="text-sm">
              Pickup service area
              <input
                required
                className="input-field w-full mt-2"
                maxLength={160}
                value={pickupArea}
                onChange={(event) => setPickupArea(event.target.value)}
                placeholder="City or general area, without exact address"
              />
            </label>
            <label className="text-sm">
              Destination service area
              <input
                required
                className="input-field w-full mt-2"
                maxLength={160}
                value={dropoffArea}
                onChange={(event) => setDropoffArea(event.target.value)}
                placeholder="City or general area"
              />
            </label>
          </div>
          <label className="text-sm block">
            Operational requirements
            <textarea
              required
              maxLength={1000}
              className="input-field w-full mt-2"
              rows={3}
              value={requirements}
              onChange={(event) => setRequirements(event.target.value)}
              placeholder="Capacity, vehicle type, accessibility, timing. No customer names or contacts."
            />
          </label>
          <p className="text-xs text-on-surface-variant">
            Exact departure and service end are required. Requests with
            additional stops require team review.
          </p>
          {!!publish.error && (
            <p role="alert" className="text-error">
              {inquiryError(publish.error)}
            </p>
          )}
          <button className="btn-primary" disabled={publish.isLoading}>
            Publish capacity request
          </button>
        </form>
      )}
    </section>
  );
}
export default function AdminTripSourcing() {
  const [view, setView] = useState<'available' | 'owned'>('available');
  const cache = useQueryClient();
  const list = useQuery(['trip-sourcing', view], () =>
    tripSourcingAPI.list(view).then((response) => response.data.data)
  );
  const requests = Array.isArray(list.data) ? list.data : [];
  return (
    <PageLayout className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Partner sourcing</h1>
        <p className="text-on-surface-variant mt-1">
          Arrange capacity and review proposals before a trip is confirmed.
        </p>
      </div>
      <div className="flex gap-3">
        <button
          className={view === 'available' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setView('available')}
        >
          Requests available to your company
        </button>
        <button
          className={view === 'owned' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setView('owned')}
        >
          Your published requests
        </button>
      </div>
      {list.isLoading && <p role="status">Loading capacity requests…</p>}
      {!!list.error && (
        <p role="alert" className="text-error">
          {inquiryError(list.error)}{' '}
          <button onClick={() => list.refetch()} className="underline">
            Retry
          </button>
        </p>
      )}
      {requests.map((request) => (
        <RequestCard
          key={`${request.id}:${request.version}`}
          request={request}
          onChange={() => cache.invalidateQueries('trip-sourcing')}
        />
      ))}
      {!list.isLoading && !list.error && !requests.length && (
        <div className="admin-subpanel rounded-card p-10 text-center">
          <h2 className="font-semibold">No capacity requests</h2>
          <p className="text-sm text-on-surface-variant mt-2">
            Publish requests from an opportunity, or return here when another
            operator needs capacity.
          </p>
        </div>
      )}
    </PageLayout>
  );
}
